import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { generateProject } from "@/lib/api";
import { Upload, X, FileText, Loader2, Sparkles, Gift } from "lucide-react";

const DEMOS = [
  {
    label: "Rénovation appartement haussmannien 85 m²",
    data: {
      name: "Rénovation appartement haussmannien 85 m²",
      project_type: "renovation",
      surface_m2: 85,
      start_date: new Date().toISOString().slice(0, 10),
      budget_estime: 145000,
      contraintes: "Immeuble occupé, accès escalier uniquement, horaires 8h-18h",
      description:
        "Rénovation complète 3 pièces: cuisine, salle de bain, électricité aux normes, parquet, peinture, moulures conservées.",
    },
  },
  {
    label: "Construction villa contemporaine Biarritz 180 m²",
    data: {
      name: "Villa contemporaine Biarritz 180 m²",
      project_type: "residentiel",
      surface_m2: 180,
      start_date: new Date().toISOString().slice(0, 10),
      budget_estime: 520000,
      contraintes: "Terrain en pente, proximité littoral, PLU strict",
      description:
        "Villa T5 R+1 toit-terrasse, ossature béton, grandes baies vitrées, piscine 8x4, jardin paysager.",
    },
  },
  {
    label: "Aménagement bureaux tertiaires Lyon 420 m²",
    data: {
      name: "Bureaux tertiaires Lyon 420 m²",
      project_type: "tertiaire",
      surface_m2: 420,
      start_date: new Date().toISOString().slice(0, 10),
      budget_estime: 380000,
      contraintes: "Immeuble en activité, travaux nuit/week-end tolérés",
      description:
        "Aménagement plateau open-space + 6 salles de réunion, cloisons vitrées, faux plafonds, réseaux VDI, climatisation.",
    },
  },
];

export default function NewProject() {
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({
    name: "",
    project_type: "residentiel",
    surface_m2: "",
    start_date: new Date().toISOString().slice(0, 10),
    budget_estime: "",
    contraintes: "",
    description: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    addFiles(dropped);
  };

  const addFiles = (list) => {
    const valid = list.filter((f) => /\.(pdf|docx|xlsx|xlsm)$/i.test(f.name));
    if (valid.length < list.length) {
      toast.warning("Certains fichiers ignorés (formats acceptés: PDF, DOCX, XLSX)");
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const loadDemo = (demo) => {
    setForm(demo.data);
    toast.success("Projet démo chargé — cliquez sur Générer");
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Veuillez saisir un nom de projet");
    if (!form.start_date) return toast.error("Date de démarrage requise");

    setLoading(true);
    setStep(1);
    try {
      const payload = {
        ...form,
        surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
        budget_estime: form.budget_estime ? Number(form.budget_estime) : null,
      };
      const timers = [
        setTimeout(() => setStep(2), 3000),
        setTimeout(() => setStep(3), 8000),
        setTimeout(() => setStep(4), 15000),
      ];
      const result = await generateProject(payload, files, email || null);
      timers.forEach(clearTimeout);
      toast.success("Planning généré !");
      nav(`/projet/${result.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération. Réessayez.");
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    "En attente",
    "Extraction des documents",
    "Analyse des lots (corps d'état)",
    "Calcul des précédences",
    "Optimisation du chemin critique",
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="new-project-page">
      <div className="mb-8">
        <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500">
          Étape 01 · Configuration
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F2137] mt-1">
          Nouveau planning de chantier
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base">
          Décrivez votre projet, déposez vos devis. L'IA fait le reste.
        </p>
      </div>

      <div
        data-testid="beta-banner"
        className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
      >
        <Gift className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <span className="font-semibold">Beta gratuite</span> — utilisez le service
          librement en échange de votre retour d'expérience à la fin. Aucun paiement, aucun
          engagement.
        </div>
      </div>

      <div
        data-testid="demo-projects"
        className="mb-6 rounded-xl border border-dashed border-[#F59E0B]/60 bg-[#F59E0B]/5 p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#B45309]" />
          <span className="font-mono-tech text-xs uppercase tracking-wider text-[#B45309]">
            Démarrer avec un projet démo
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {DEMOS.map((d, i) => (
            <Button
              key={i}
              variant="outline"
              size="sm"
              className="bg-white"
              data-testid={`demo-${i}`}
              onClick={() => loadDemo(d)}
            >
              {d.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
          <div>
            <Label htmlFor="name">Nom du projet *</Label>
            <Input
              id="name"
              data-testid="input-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ex: Rénovation appartement rue Lafayette"
              className="mt-1.5"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Type de projet</Label>
              <Select
                value={form.project_type}
                onValueChange={(v) => set("project_type", v)}
              >
                <SelectTrigger data-testid="select-type" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="residentiel">Résidentiel neuf</SelectItem>
                  <SelectItem value="tertiaire">Tertiaire (bureaux)</SelectItem>
                  <SelectItem value="renovation">Rénovation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="surface">Surface (m²)</Label>
              <Input
                id="surface"
                data-testid="input-surface"
                type="number"
                value={form.surface_m2}
                onChange={(e) => set("surface_m2", e.target.value)}
                placeholder="120"
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start">Date de démarrage *</Label>
              <Input
                id="start"
                data-testid="input-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="budget">Budget estimé (€)</Label>
              <Input
                id="budget"
                data-testid="input-budget"
                type="number"
                value={form.budget_estime}
                onChange={(e) => set("budget_estime", e.target.value)}
                placeholder="150000"
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="constraints">Contraintes particulières</Label>
            <Textarea
              id="constraints"
              data-testid="input-constraints"
              value={form.contraintes}
              onChange={(e) => set("contraintes", e.target.value)}
              placeholder="Accès, horaires, occupation, PLU, intempéries..."
              className="mt-1.5"
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="desc">Description libre</Label>
            <Textarea
              id="desc"
              data-testid="input-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Contexte, spécificités techniques, exigences..."
              className="mt-1.5"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="email">
              Votre email <span className="text-slate-400 font-normal text-xs">(optionnel — pour vous recontacter sur votre retour)</span>
            </Label>
            <Input
              id="email"
              data-testid="input-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              className="mt-1.5"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div
            data-testid="dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-[#F59E0B] bg-[#F59E0B]/5"
                : "border-slate-300 bg-white hover:border-[#F59E0B]/70"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.xlsm"
              className="hidden"
              onChange={(e) => addFiles(Array.from(e.target.files || []))}
              data-testid="file-input"
            />
            <Upload className="w-8 h-8 text-[#0F2137] mx-auto" />
            <div className="mt-3 font-display font-semibold text-[#0F2137]">
              Déposer devis / documents
            </div>
            <div className="text-xs text-slate-500 mt-1">
              PDF · DOCX · XLSX · Optionnel
            </div>
          </div>

          {files.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="files-list">
              <div className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                {files.length} fichier{files.length > 1 ? "s" : ""}
              </div>
              <div className="space-y-2">
                {files.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`file-item-${i}`}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="truncate">{f.name}</span>
                      <Badge variant="secondary" className="font-mono-tech text-[10px]">
                        {(f.size / 1024).toFixed(0)} ko
                      </Badge>
                    </div>
                    <button
                      onClick={() => removeFile(i)}
                      data-testid={`remove-file-${i}`}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={submit}
            disabled={loading}
            data-testid="btn-generate"
            className="w-full h-12 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold text-base"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Générer gratuitement
              </>
            )}
          </Button>

          {loading && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="progress-panel">
              {steps.slice(1).map((s, i) => {
                const active = step > i;
                const current = step === i + 1;
                return (
                  <div key={i} className="flex items-center gap-3 py-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        active ? "bg-[#F59E0B]" : "bg-slate-300"
                      } ${current ? "animate-pulse" : ""}`}
                    />
                    <span
                      className={`text-sm ${
                        active ? "text-[#0F2137] font-medium" : "text-slate-400"
                      }`}
                    >
                      {s}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
